
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
import Rocket from 'plaid-threads/Icons/Rocket';
export const metadata = {
  toc: true,
  description: 'Learn how to fetch Transactions data for your users'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Transactions to your app' subtitle={metadata.description} mdxType="Header" />
    <Callout icon={<Rocket mdxType="Rocket" />} mdxType="Callout">
      <p>{`See `}<a parentName="p" {...{
          "href": "https://github.com/plaid/pattern"
        }}>{`Plaid Pattern`}</a>{` for a sample app that uses Plaid's Transactions product for the personal financial management use case.`}</p>
    </Callout>
    <p>{`In this guide, we'll start from scratch and walk through how to use Transactions to perform an initial fetch of a user's transaction history. If you are already familiar with using Plaid and are set up to make calls to the Plaid API, you can skip ahead to `}<a parentName="p" {...{
        "href": "#fetching-transaction-data"
      }}>{`Fetching transaction data`}</a>{`.`}</p>
    <p>{`For a detailed, step-by-step view, you can also watch our full-length, comprehensive tutorial walkthrough on integrating transactions.`}</p>
    <YouTubeEmbed videoId='Pin0-ceDKcI' alignment='full' title='Plaid Transactions full integration tutorial' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` on the Dashboard. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`. Your application profile and company profile must be completed before connecting to certain institutions in Production.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-an-item-with-link"
    }}>{`Create an Item with Link`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant authentication flow
for each institution that Plaid supports. Link makes it secure and easy for users to
connect their bank accounts to Plaid. Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`.`}</p>
    <p>{`Using Link, we will create a Plaid `}<em parentName="p">{`Item`}</em>{`, which is a Plaid term for a login at a financial institution. An Item is not the same as a financial institution account, although every account will be associated with an Item. For example, if a user has one login at their bank that allows them to access both their checking account and their savings account, a single Item would be associated with both of those accounts. If you want to customize Link's look and feel, you can do so from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Dashboard`}</a>{`.`}</p>
    <p>{`Before initializing Link, you will need to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on the server side of your application.
A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, one-time use token that is used to authenticate your app with Link.
You can create one using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. Then, on the client side of your application, you'll need to initialize Link with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` that you just created.`}</p>
    <Callout mdxType="Callout">
      <p>{`You can use `}<a parentName="p" {...{
          "href": "/docs/link/multi-item-link"
        }}>{`Multi-Item Link`}</a>{` to let users link multiple institutions in a single Link session, rather than one at a time across separate sessions.`}</p>
    </Callout>
    <Callout mdxType="Callout">
      <p>{`The `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` sample code below will create an Item with a maximum of 90 days of transaction history. To request more, set the `}<inlineCode parentName="p">{`transactions.days_requested`}</inlineCode>{` parameter in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request.`}</p>
    </Callout>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <ContentSwitcher.Container id='transactions_multi_item' names={['Single institution', 'Multiple institutions (Multi-Item Link)']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <SampleCode.CreateLinkToken product='transactions' />
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`Multi-Item Link requires a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` first, then pass that `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` (instead of a `}<inlineCode parentName="p">{`user`}</inlineCode>{` object) to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` along with `}<inlineCode parentName="p">{`enable_multi_item_link: true`}</inlineCode>{`.`}</p>
        <MDXCodeBlock type='server' title='/user/create' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`app.post('/api/create_plaid_user', async function (request, response) {
  // Get the client_user_id by searching for the current user
  const user = await User.find(...);
  const clientUserId = user.id;
  const userResponse = await client.userCreate({
    client_user_id: clientUserId,
  });
  const plaidUserId = userResponse.data.user_id;
  await User.update(user.id, { plaidUserId });
  response.json({ user_id: plaidUserId });
});
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/user/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_user_id": "\${UNIQUE_USER_ID}"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`post '/api/create_plaid_user' do
  current_user = User.find(...)
  client_user_id = current_user.id

  request = Plaid::UserCreateRequest.new(
    { client_user_id: client_user_id }
  )
  response = client.user_create(request)
  plaid_user_id = response.user_id
  current_user.update(plaid_user_id: plaid_user_id)

  content_type :json
  { user_id: plaid_user_id }.to_json
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`String clientUserId = userFromDB.id;

UserCreateRequest request = new UserCreateRequest()
  .clientUserId(clientUserId);

UserCreateResponse response = plaidClient
  .userCreate(request, null)
  .execute()
  .body();

String plaidUserId = response.getUserId();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`client_user_id = user.id

request = UserCreateRequest(
    client_user_id=client_user_id
)
response = client.user_create(request)
plaid_user_id = response['user_id']
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`clientUserId := user.ID.String()

request := plaid.NewUserCreateRequest(clientUserId)
resp, _, err := client.PlaidApi.UserCreate(ctx).
  UserCreateRequest(*request).
  Execute()
plaidUserId := resp.GetUserId()
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`clientUserId := user.ID.String()

request := &plaid.UserCreateRequest{
  ClientUserID: clientUserId,
}
resp, err := client.User.Create(ctx, request)
plaidUserId := resp.GetUserID()
`}</code></pre>
        </MDXCodeBlock>
        <MDXCodeBlock type='server' title='/link/token/create' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`app.post('/api/create_link_token', async function (request, response) {
  // plaidUserId was returned by /user/create in the previous step
  const user = await User.find(...);
  const plaidUserId = user.plaidUserId;
  const linkTokenRequest = {
    user_id: plaidUserId,
    enable_multi_item_link: true,
    client_name: 'Plaid Test App',
    products: ['transactions'],
    language: 'en',
    webhook: 'https://webhook.example.com',
    redirect_uri: 'https://domainname.com/oauth-page.html',
    country_codes: ['US'],
  };
  try {
    const createTokenResponse = await client.linkTokenCreate(linkTokenRequest);
    response.json(createTokenResponse.data);
  } catch (error) {
    // handle error
  }
});
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_name": "Plaid Test App",
  "user_id": "\${PLAID_USER_ID}",
  "enable_multi_item_link": true,
  "products": ["transactions"],
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://webhook.example.com",
  "redirect_uri": "https://domainname.com/oauth-page.html"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`post '/api/create_link_token' do
  # plaid_user_id was returned by /user/create in the previous step
  plaid_user_id = current_user.plaid_user_id

  request = Plaid::LinkTokenCreateRequest.new(
    {
      user_id: plaid_user_id,
      enable_multi_item_link: true,
      client_name: 'Plaid Test App',
      products: ['transactions'],
      country_codes: ['US'],
      language: "en",
      redirect_uri: nil_if_empty_envvar('PLAID_REDIRECT_URI'),
      webhook: 'https://webhook.example.com'
    }
  )
  response = client.link_token_create(request)
  content_type :json
  response.to_json
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
String plaidUserId = userFromDB.plaidUserId;

LinkTokenCreateRequest request = new LinkTokenCreateRequest()
  .userId(plaidUserId)
  .enableMultiItemLink(true)
  .clientName("Plaid Test App")
  .products(Arrays.asList(Products.TRANSACTIONS))
  .countryCodes(Arrays.asList(CountryCode.US))
  .language("en")
  .redirectUri("https://domainname.com/oauth-page.html")
  .webhook("https://webhook.example.com");

LinkTokenCreateResponse response = plaidClient
  .linkTokenCreate(request)
  .execute()
  .body();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`# plaid_user_id was returned by /user/create in the previous step
plaid_user_id = user.plaid_user_id

request = LinkTokenCreateRequest(
        products=[Products('transactions')],
        client_name="Plaid Test App",
        country_codes=[CountryCode('US')],
        redirect_uri='https://domainname.com/oauth-page.html',
        language='en',
        webhook='https://webhook.example.com',
        user_id=plaid_user_id,
        enable_multi_item_link=True,
    )
response = client.link_token_create(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
plaidUserId := user.PlaidUserId

request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test App",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUserId(plaidUserId)
request.SetEnableMultiItemLink(true)
request.SetWebhook("https://webhook.example.com")
request.SetRedirectUri("https://domainname.com/oauth-page.html")
request.SetProducts([]plaid.Products{plaid.PRODUCTS_TRANSACTIONS})

resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).
  LinkTokenCreateRequest(*request).
  Execute()
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
plaidUserId := user.PlaidUserId

request := &plaid.LinkTokenCreateRequest{
  ClientName: "Plaid Test App",
  Language: "en",
  CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
  UserID: plaid.String(plaidUserId),
  EnableMultiItemLink: plaid.Bool(true),
  Webhook: plaid.String("https://webhook.example.com"),
  RedirectURI: plaid.String("https://domainname.com/oauth-page.html"),
  Products: []plaid.Products{plaid.ProductsTransactions},
}

resp, err := client.Link.CreateToken(ctx, request)
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h5 {...{
      "id": "install-link-dependency"
    }}>{`Install Link dependency`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "#index.html",
        "#index.html": true
      }}>{`<head>
  <title>Connect a bank</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <ContentSwitcher.Container id='transactions_multi_item' names={['Single institution', 'Multiple institutions (Multi-Item Link)']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #app.js",
            "switcher": "false",
            "#app.js": true
          }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token to your app server.
    $.post('/exchange_public_token', {
      public_token: public_token,
    });
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`With Multi-Item Link, `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` will be called but will not contain public tokens. Instead, get them either from the `}<a parentName="p" {...{
            "href": "/docs/api/link/#session_finished"
          }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook or by calling `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` after `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` fires.`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #app.js",
            "switcher": "false",
            "#app.js": true
          }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // public_token will not be populated here; public_tokens are
    // retrieved via the SESSION_FINISHED webhook or /link/token/get instead.
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node",
            "metastring": "switcher=false #webhookServer.js",
            "switcher": "false",
            "#webhookServer.js": true
          }}>{`app.post('/server/receive_webhook', async (req, res, next) => {
  const { webhook_type, webhook_code, status, public_tokens } = req.body;
  const isMultiItemSessionFinished =
    webhook_type === 'LINK' &&
    webhook_code === 'SESSION_FINISHED' &&
    status === 'success';
  if (isMultiItemSessionFinished) {
    // Exchange each token in public_tokens for an access_token
    // via /item/public_token/exchange.
  }
  // Handle other types of webhooks
});
`}</code></pre>
        <p>{`Alternatively, call `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` and read the public tokens from `}<inlineCode parentName="p">{`link_sessions[].results.item_add_results[]`}</inlineCode>{` in the response.`}</p>
        <MDXCodeBlock type='server' title='/link/token/get' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`const request = {
  link_token: linkToken,
};
const response = await client.linkTokenGet(request);
const publicTokens = response.data.link_sessions
  .flatMap((session) => session.results.item_add_results)
  .map((result) => result.public_token);
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/link/token/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "link_token": "\${LINK_TOKEN}"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`request = Plaid::LinkTokenGetRequest.new({ link_token: link_token })
response = client.link_token_get(request)
public_tokens = response.link_sessions.flat_map do |session|
  session.results.item_add_results.map(&:public_token)
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`LinkTokenGetRequest request = new LinkTokenGetRequest()
  .linkToken(linkToken);
LinkTokenGetResponse response = plaidClient
  .linkTokenGet(request)
  .execute()
  .body();

List<String> publicTokens = new ArrayList<>();
for (LinkTokenGetSessionsResponse session : response.getLinkSessions()) {
  LinkSessionResults results = session.getResults();
  for (LinkSessionItemAddResult result : results.getItemAddResults()) {
    publicTokens.add(result.getPublicToken());
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`request = LinkTokenGetRequest(link_token=link_token)
response = client.link_token_get(request)
public_tokens = [
    result['public_token']
    for session in response['link_sessions']
    for result in session['results']['item_add_results']
]
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`linkTokenGetRequest := plaid.NewLinkTokenGetRequest(linkToken)
linkTokenGetResp, _, err := client.PlaidApi.LinkTokenGet(ctx).
  LinkTokenGetRequest(*linkTokenGetRequest).
  Execute()

var publicTokens []string
for _, session := range linkTokenGetResp.GetLinkSessions() {
  results := session.GetResults()
  for _, result := range results.GetItemAddResults() {
    publicTokens = append(publicTokens, result.GetPublicToken())
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`linkTokenGetRequest := &plaid.LinkTokenGetRequest{
  LinkToken: linkToken,
}
linkTokenGetResp, err := client.Link.GetToken(ctx, linkTokenGetRequest)

var publicTokens []string
for _, session := range linkTokenGetResp.GetLinkSessions() {
  results := session.GetResults()
  for _, result := range results.GetItemAddResults() {
    publicTokens = append(publicTokens, result.GetPublicToken())
  }
}
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "get-a-persistent-access-token"
    }}>{`Get a persistent access token`}</h4>
    <p>{`Next, on the server side, we need to exchange our `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`(s) for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` for each linked Item. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will allow us to make authenticated calls to the Plaid API. Doing so is as easy as calling the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` endpoint from our server-side handler. We'll use the client library we configured earlier to make the API call.`}</p>
    <p>{`Save each `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in a secure datastore, as they're used to access Item data and identify webhooks, respectively. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will remain valid unless you actively choose to expire it via rotation or remove the corresponding Item via `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` should be stored securely, and never in client-side code. A `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` is a one-time use token with a lifetime of 30 minutes, so there is no need to store it.`}</p>
    <SampleCode.ExchangePublicToken />
    <p>{`If you used Multi-Item Link, call `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` once per token in your `}<inlineCode parentName="p">{`public_tokens`}</inlineCode>{` array, saving the resulting `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` pair each time.`}</p>
    <h4 {...{
      "id": "fetching-transaction-data"
    }}>{`Fetching transaction data`}</h4>
    <p>{`Now that the authentication step is out of the way, we can begin using authenticated endpoints from the Plaid API and fetch transaction data using the `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` endpoint.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` endpoint is used to both initialize your view of transactions data, and keep you current with any changes that have occurred. When you first call it on an Item with no `}<inlineCode parentName="p">{`cursor`}</inlineCode>{` parameter, transactions data available at that time is returned. If more updates are available than requested with the `}<inlineCode parentName="p">{`count`}</inlineCode>{` parameter (maximum of 500), `}<inlineCode parentName="p">{`has_more`}</inlineCode>{` will be set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`, indicating the endpoint should be called again, using the `}<inlineCode parentName="p">{`next_cursor`}</inlineCode>{` from the previous response in the `}<inlineCode parentName="p">{`cursor`}</inlineCode>{` field of the next request, to receive another page of data. After successfully pulling all currently available pages, you can store the cursor for later requests, allowing Plaid to send you new updates from when you last queried the endpoint.`}</p>
    <p>{`Note that if you encounter an error during pagination, it's important to restart the pagination loop from the beginning. For more details, see the documentation for `}<a parentName="p" {...{
        "href": "/docs/errors/transactions/#transactions_sync_mutation_during_pagination"
      }}><inlineCode parentName="a">{`TRANSACTIONS_SYNC_MUTATION_DURING_PAGINATION`}</inlineCode></a>{`. For sample code for handling the error, see the `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` API reference.`}</p>
    <p>{`Typically, the first 30 days of transaction history is available to be fetched almost immediately, but full transaction history may take a minute or more to become available. If you get an empty response when calling `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` shortly after linking an Item, it's likely that the first 30 days of transaction history has not yet been pulled. You will need to call the endpoint when the data is pulled. Similarly, if you only get the first 30 days of transaction history, you will need to wait until it is complete, and call the endpoint again.`}</p>
    <p>{`To be notified whenever additional data becomes available, see `}<a parentName="p" {...{
        "href": "/docs/transactions/webhooks"
      }}>{`Transaction webhooks`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='/transactions/sync' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`// Provide a cursor from your database if you've previously
// received one for the Item. Leave null if this is your
// first sync call for this Item. The first request will
// return a cursor.
let cursor = database.getLatestCursorOrNull(itemId);

// New transaction updates since "cursor"
let added: Array<Transaction> = [];
let modified: Array<Transaction> = [];
// Removed transaction ids
let removed: Array<RemovedTransaction> = [];
let hasMore = true;

// Iterate through each page of new transaction updates for item
while (hasMore) {
  const request: TransactionsSyncRequest = {
    access_token: accessToken,
    cursor: cursor,
  };
  const response = await client.transactionsSync(request);
  const data = response.data;

  // Add this page of results
  added = added.concat(data.added);
  modified = modified.concat(data.modified);
  removed = removed.concat(data.removed);

  hasMore = data.has_more;

  // Update cursor to the next cursor
  cursor = data.next_cursor;
}

// Persist cursor and updated data
database.applyUpdates(itemId, added, modified, removed, cursor);
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/transactions/sync \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": String,
  "cursor": String,
  "count": 250
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# Provide a cursor from your database if you've previously
# received one for the Item. Leave null if this is your
# first sync call for this item. The first request will
# return a cursor.
cursor = database.get_latest_cursor_or_none(item_id)

# New transaction updates since "cursor"
added = []
modified = []
removed = [] # Removed transaction ids
has_more = true

# Iterate through each page of new transaction updates for item
while has_more
  request = Plaid::TransactionsSyncRequest.new(
    {
      access_token: access_token,
      cursor: cursor
    }
  )

  response = client.transactions_sync(request)

  # Add this page of results
  added += response.added
  modified += response.modified
  removed += response.removed

  has_more = response.has_more

  # Update cursor to the next cursor
  cursor = response.next_cursor
end

# Persist cursor and updated data
database.apply_updates(item_id, added, modified, removed, cursor)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`// Provide a cursor from your database if you've previously
// received one for the item leave null if this is your
// first sync call for this item. The first request will
// return a cursor.
String cursor = database.getLatestCursorOrNull(itemId);

// New transaction updates since "cursor"
List<Transaction> added = new ArrayList<Transaction>();
List<Transaction> modified = new ArrayList<Transaction>();
List<RemovedTransaction> removed = new ArrayList<RemovedTransaction>();
boolean hasMore = true;

// Iterate through each page of new transaction updates for item
while (hasMore) {
  TransactionsSyncRequest request = new TransactionsSyncRequest()
    .accessToken(accessToken)
    .cursor(cursor);

  TransactionsSyncResponse response = plaidClient.transactionsSync(request).execute().body();

  // Add this page of results
  added.addAll(response.getAdded());
  modified.addAll(response.getModified());
  removed.addAll(response.getRemoved());

  hasMore = response.getHasMore();

  // Update cursor to the next cursor
  cursor = response.getNextCursor();
}
// Persist cursor and updated data
database.applyUpdates(itemId, added, modified, removed, cursor);
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`# Provide a cursor from your database if you've previously
# received one for the Item. Leave null if this is your
# first sync call for this Item. The first request will
# return a cursor.
cursor = database.get_latest_cursor_or_none(item_id)

# New transaction updates since "cursor"
added = []
modified = []
removed = [] # Removed transaction ids
has_more = True

# Iterate through each page of new transaction updates for item
while has_more:
  request = TransactionsSyncRequest(
    access_token=access_token,
    cursor=cursor,
  )
  response = plaid_client.transactions_sync(request)

  # Add this page of results
  added.extend(response['added'])
  modified.extend(response['modified'])
  removed.extend(response['removed'])

  has_more = response['has_more']

  # Update cursor to the next cursor
  cursor = response['next_cursor']

# Persist cursor and updated data
database.apply_updates(item_id, added, modified, removed, cursor)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`// Provide a cursor from your database if you've previously
// received one for the Item. Leave null if this is your
// first sync call for this Item. The first request will
// return a cursor.
cursor := database.getLatestCursorOrNil(itemId)

// New transaction updates since "cursor"
var added []plaid.Transaction
var modified []plaid.Transaction
var removed []plaid.RemovedTransaction // Removed transaction ids
hasMore := true

// Iterate through each page of new transaction updates for item
for hasMore {
    request := plaid.NewTransactionsSyncRequest(accessToken)
    if cursor != nil {
        request.SetCursor(*cursor)
    }
    resp, _, err := client.PlaidApi.TransactionsSync(
        ctx,
    ).TransactionsSyncRequest(*request).Execute()

    // Add this page of results
    added = append(added, resp.GetAdded()...)
    modified = append(modified, resp.GetModified()...)
    removed = append(removed, resp.GetRemoved()...)

    hasMore = resp.GetHasMore()

    // Update cursor to the next cursor
    nextCursor := resp.GetNextCursor()
    cursor = &nextCursor
}

// Persist cursor and updated data
database.applyUpdates(itemId, added, modified, removed, cursor)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`// Provide a cursor from your database if you've previously
// received one for the Item. Leave null if this is your
// first sync call for this Item. The first request will
// return a cursor.
cursor := database.getLatestCursorOrNil(itemId)

// New transaction updates since "cursor"
var added []*plaid.Transaction
var modified []*plaid.Transaction
var removed []*plaid.RemovedTransaction // Removed transaction ids
hasMore := true

// Iterate through each page of new transaction updates for item
for hasMore {
    request := &plaid.TransactionsSyncRequest{AccessToken: accessToken}
    if cursor != nil {
        request.Cursor = cursor
    }
    resp, err := client.Transactions.Sync(ctx, request)

    // Add this page of results
    added = append(added, resp.GetAdded()...)
    modified = append(modified, resp.GetModified()...)
    removed = append(removed, resp.GetRemoved()...)

    hasMore = resp.GetHasMore()

    // Update cursor to the next cursor
    nextCursor := resp.GetNextCursor()
    cursor = &nextCursor
}

// Persist cursor and updated data
database.applyUpdates(itemId, added, modified, removed, cursor)
`}</code></pre>
    </MDXCodeBlock>
    <h4 {...{
      "id": "updating-transaction-data"
    }}>{`Updating transaction data`}</h4>
    <p>{`After your initial `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{` request, you may want your application to be notified when any transactions are added, removed, or modified in order to immediately fetch them from `}<inlineCode parentName="p">{`/transactions/sync`}</inlineCode>{`. To learn how, see `}<a parentName="p" {...{
        "href": "/docs/transactions/webhooks"
      }}>{`Transaction Webhooks`}</a>{`.`}</p>
    <h4 {...{
      "id": "example-code-in-plaid-pattern"
    }}>{`Example code in Plaid Pattern`}</h4>
    <p>{`For a real-life example of an app that incorporates transactions, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern"
      }}>{`Plaid Pattern`}</a>{` sample app. Pattern is a sample financial management app that fetches transactions data upon receipt of transactions webhooks. Transactions code in Plaid Pattern can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern/blob/master/server/webhookHandlers/handleTransactionsWebhook.js"
      }}>{`handleTransactionsWebhook.js`}</a>{`.`}</p>
    <h4 {...{
      "id": "fetching-by-date"
    }}>{`Fetching by date`}</h4>
    <p>{`If you want to fetch transactions data by date range, you can use the `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{` endpoint.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-an-item-with-link",
        level: 4,
        title: "Create an Item with Link",
      },
{
        id: "get-a-persistent-access-token",
        level: 4,
        title: "Get a persistent access token",
      },
{
        id: "fetching-transaction-data",
        level: 4,
        title: "Fetching transaction data",
      },
{
        id: "updating-transaction-data",
        level: 4,
        title: "Updating transaction data",
      },
{
        id: "example-code-in-plaid-pattern",
        level: 4,
        title: "Example code in Plaid Pattern",
      },
{
        id: "fetching-by-date",
        level: 4,
        title: "Fetching by date",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
